import { useEffect, useState } from "react";
import { Users, TrendingUp, CreditCard, UserCheck, Trash2, Sparkles, Ban, Check, CalendarPlus, Gift } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";

const STATUS_LABEL = {
    active: { label: "Attivo", cls: "bg-forest text-white" },
    trialing: { label: "Trial", cls: "bg-blue-100 text-blue-700" },
    canceled: { label: "Annullato", cls: "bg-orange-100 text-orange-700" },
    expired: { label: "Scaduto", cls: "bg-red-100 text-red-700" },
};

function StatCard({ icon: Icon, label, value, sub, tint }) {
    return (
        <div className="card-soft p-5">
            <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold uppercase tracking-wider text-stone-500">{label}</span>
                <div className={`w-9 h-9 rounded-xl grid place-items-center ${tint}`}>
                    <Icon size={18} />
                </div>
            </div>
            <div className="font-display text-3xl font-semibold text-forest-dark">{value}</div>
            {sub && <div className="text-xs text-stone-500 mt-1">{sub}</div>}
        </div>
    );
}

export default function Admin() {
    const [stats, setStats] = useState(null);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);

    async function load() {
        setLoading(true);
        try {
            const [s, u] = await Promise.all([api.get("/admin/stats"), api.get("/admin/users")]);
            setStats(s.data);
            setUsers(u.data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setLoading(false);
        }
    }
    useEffect(() => {
        load();
    }, []);

    async function setStatus(id, status) {
        try {
            await api.patch(`/admin/users/${id}`, { subscription_status: status });
            toast.success("Stato aggiornato");
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        }
    }

    async function delUser(id, email) {
        if (!window.confirm(`Eliminare l'utente ${email}? L'azione è irreversibile.`)) return;
        try {
            await api.delete(`/admin/users/${id}`);
            toast.success("Utente eliminato");
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        }
    }

    async function extendTrial(id, email) {
        const raw = window.prompt(`Estendi il trial di ${email}: quanti giorni?`, "14");
        if (!raw) return;
        const days = parseInt(raw, 10);
        if (!days || days < 1 || days > 365) {
            toast.error("Inserisci un numero tra 1 e 365");
            return;
        }
        try {
            const r = await api.post(`/admin/users/${id}/extend-trial`, { days });
            const until = new Date(r.data.trial_ends_at).toLocaleDateString("it-IT");
            toast.success(`Trial esteso fino al ${until}`);
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        }
    }

    async function grantFree(id, email) {
        const raw = window.prompt(`Periodo gratis per ${email}: quanti giorni di abbonamento attivo?`, "30");
        if (!raw) return;
        const days = parseInt(raw, 10);
        if (!days || days < 1 || days > 3650) {
            toast.error("Inserisci un numero tra 1 e 3650");
            return;
        }
        if (!window.confirm(`Concedere ${days} giorni gratis a ${email}? L'utente avrà accesso completo come abbonato.`)) return;
        try {
            const r = await api.post(`/admin/users/${id}/grant-free`, { days });
            const until = new Date(r.data.free_until).toLocaleDateString("it-IT");
            toast.success(`Accesso gratis attivato fino al ${until}`);
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        }
    }

    if (loading) return <div className="text-stone-500 text-sm">Caricamento...</div>;
    if (!stats) return null;

    return (
        <div className="space-y-6" data-testid="admin-page">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <StatCard icon={Users} label="Utenti totali" value={stats.total_users} tint="bg-forest-50 text-forest" />
                <StatCard
                    icon={TrendingUp}
                    label="MRR stimato"
                    value={`${stats.mrr_eur.toFixed(2)} €`}
                    sub={`${stats.paying} clienti paganti`}
                    tint="bg-orange-50 text-terracotta"
                />
                <StatCard icon={Sparkles} label="In prova" value={stats.trialing} tint="bg-blue-50 text-blue-600" />
                <StatCard
                    icon={CreditCard}
                    label="Pagamenti (30g)"
                    value={stats.payments_30d}
                    tint="bg-purple-50 text-purple-600"
                />
            </div>

            <div className="card-soft p-6">
                <div className="flex items-center gap-2 mb-4">
                    <UserCheck size={18} className="text-forest" />
                    <h2 className="font-display text-xl text-forest-dark">Utenti registrati</h2>
                </div>

                {/* Mobile card view */}
                <div className="sm:hidden space-y-3">
                    {users.map((u) => {
                        const st = STATUS_LABEL[u.subscription_status] || { label: u.subscription_status || "—", cls: "bg-stone-100 text-stone-600" };
                        return (
                            <div key={u.id} className="border border-soft rounded-xl p-3" data-testid={`admin-user-card-${u.id}`}>
                                <div className="flex items-start justify-between mb-2">
                                    <div className="min-w-0 flex-1">
                                        <p className="font-medium text-stone-700 break-all text-sm">{u.email}</p>
                                        <p className="text-xs text-stone-500 mt-0.5">{u.name || "—"}</p>
                                    </div>
                                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full whitespace-nowrap ml-2 ${st.cls}`}>{st.label}</span>
                                </div>
                                <p className="text-xs text-stone-500 mb-3">
                                    {u.role === "admin" ? "ADMIN · " : ""}
                                    {u.created_at ? new Date(u.created_at).toLocaleDateString("it-IT") : "—"}
                                </p>
                                {u.role !== "admin" && (
                                    <div className="flex flex-wrap gap-2">
                                        {u.subscription_status !== "active" && (
                                            <button
                                                onClick={() => setStatus(u.id, "active")}
                                                className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-forest-50 text-forest text-xs font-semibold"
                                                data-testid={`admin-activate-${u.id}`}
                                            >
                                                <Check size={14} /> Attiva
                                            </button>
                                        )}
                                        <button
                                            onClick={() => extendTrial(u.id, u.email)}
                                            className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-blue-50 text-blue-700 text-xs font-semibold"
                                            data-testid={`admin-extend-${u.id}`}
                                        >
                                            <CalendarPlus size={14} /> Estendi
                                        </button>
                                        <button
                                            onClick={() => grantFree(u.id, u.email)}
                                            className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-purple-50 text-purple-700 text-xs font-semibold"
                                            data-testid={`admin-grant-${u.id}`}
                                        >
                                            <Gift size={14} /> Regala
                                        </button>
                                        {u.subscription_status !== "canceled" && (
                                            <button
                                                onClick={() => setStatus(u.id, "canceled")}
                                                className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-orange-50 text-orange-600 text-xs font-semibold"
                                                data-testid={`admin-cancel-${u.id}`}
                                            >
                                                <Ban size={14} /> Annulla
                                            </button>
                                        )}
                                        <button
                                            onClick={() => delUser(u.id, u.email)}
                                            className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-red-50 text-red-600 text-xs font-semibold"
                                            data-testid={`admin-delete-${u.id}`}
                                        >
                                            <Trash2 size={14} /> Elimina
                                        </button>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Desktop table view */}
                <div className="hidden sm:block overflow-x-auto -mx-2">
                    <table className="w-full text-sm" data-testid="admin-users-table">
                        <thead className="text-left text-xs uppercase tracking-wider text-stone-500 border-b border-soft">
                            <tr>
                                <th className="px-3 py-3">Email</th>
                                <th className="px-3 py-3">Nome</th>
                                <th className="px-3 py-3">Ruolo</th>
                                <th className="px-3 py-3">Stato</th>
                                <th className="px-3 py-3">Registrato</th>
                                <th className="px-3 py-3 text-right">Azioni</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map((u) => {
                                const st = STATUS_LABEL[u.subscription_status] || { label: u.subscription_status || "—", cls: "bg-stone-100 text-stone-600" };
                                return (
                                    <tr key={u.id} className="border-b border-soft last:border-0" data-testid={`admin-user-row-${u.id}`}>
                                        <td className="px-3 py-3 font-medium text-stone-700 break-all">{u.email}</td>
                                        <td className="px-3 py-3 text-stone-600">{u.name || "—"}</td>
                                        <td className="px-3 py-3">
                                            {u.role === "admin" ? (
                                                <span className="text-xs font-bold uppercase tracking-wider text-accent-warm">Admin</span>
                                            ) : (
                                                <span className="text-xs text-stone-500">User</span>
                                            )}
                                        </td>
                                        <td className="px-3 py-3">
                                            <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${st.cls}`}>{st.label}</span>
                                        </td>
                                        <td className="px-3 py-3 text-xs text-stone-500">
                                            {u.created_at ? new Date(u.created_at).toLocaleDateString("it-IT") : "—"}
                                        </td>
                                        <td className="px-3 py-3 text-right">
                                            <div className="inline-flex items-center gap-1.5 flex-wrap justify-end">
                                                {u.role !== "admin" && u.subscription_status !== "active" && (
                                                    <button
                                                        onClick={() => setStatus(u.id, "active")}
                                                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-forest-50 hover:bg-forest-100 text-forest text-xs font-semibold"
                                                        title="Attiva manualmente"
                                                        data-testid={`admin-activate-${u.id}`}
                                                    >
                                                        <Check size={14} /> Attiva
                                                    </button>
                                                )}
                                                {u.role !== "admin" && (
                                                    <button
                                                        onClick={() => extendTrial(u.id, u.email)}
                                                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-700 text-xs font-semibold"
                                                        title="Estendi il periodo di prova"
                                                        data-testid={`admin-extend-${u.id}`}
                                                    >
                                                        <CalendarPlus size={14} /> Estendi
                                                    </button>
                                                )}
                                                {u.role !== "admin" && (
                                                    <button
                                                        onClick={() => grantFree(u.id, u.email)}
                                                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-purple-50 hover:bg-purple-100 text-purple-700 text-xs font-semibold"
                                                        title="Regala accesso gratuito"
                                                        data-testid={`admin-grant-${u.id}`}
                                                    >
                                                        <Gift size={14} /> Regala
                                                    </button>
                                                )}
                                                {u.role !== "admin" && u.subscription_status !== "canceled" && (
                                                    <button
                                                        onClick={() => setStatus(u.id, "canceled")}
                                                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-orange-50 hover:bg-orange-100 text-orange-600 text-xs font-semibold"
                                                        title="Annulla abbonamento"
                                                        data-testid={`admin-cancel-${u.id}`}
                                                    >
                                                        <Ban size={14} /> Annulla
                                                    </button>
                                                )}
                                                {u.role !== "admin" && (
                                                    <button
                                                        onClick={() => delUser(u.id, u.email)}
                                                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 text-xs font-semibold"
                                                        title="Elimina utente"
                                                        data-testid={`admin-delete-${u.id}`}
                                                    >
                                                        <Trash2 size={14} /> Elimina
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
